import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SITE = "Dora Fshatit";
const BASE_URL = "https://dorafshatit.vercel.app";

const HOME_META = {
  al: {
    title:
      "Dora Fshatit | Ushqim i freskët, fruta të freskëta dhe perime nga ferma",
    desc:
      "Dora Fshatit lidh familjet me ferma shqiptare për ushqim të freskët, fruta të freskëta, perime të freskëta dhe shporta sezonale me dorëzim në shtëpi.",
    keywords:
      "dora fshatit, dora e fshatit, ushqim i freskët, ushqim i fresket, fruta të freskëta, fruta te fresketa, perime të freskëta, perime te fresketa, perime fshati, fruta fshati, ferma shqiptare, ferma lokale, shporta me fruta, shporta me perime, produkte fshati, ushqim nga ferma",
  },
  us: {
    title: "Dora Fshatit | Fresh fruit, vegetables and farm baskets from Albania",
    desc:
      "Shop fresh produce, fruit, vegetables, farm baskets and local Albanian goods delivered directly from partner farms to your door.",
    keywords:
      "Dora Fshatit, Dora e Fshatit, Albanian farms, fresh produce, fruit baskets, vegetable baskets, farm delivery, local groceries, fresh fruit, fresh vegetables",
  },
};

const DEFAULT_KEYWORDS = {
  al: "dora fshatit, ferma shqiptare, produkte të freskëta, fruta, perime, shporta, ushqim nga ferma",
  us: "Dora Fshatit, Albanian farms, fresh produce, farm baskets, local groceries",
};

const routeMeta = {
  "/": { titleKey: "seo.home.title", descKey: "seo.home.desc" },
  "/farms": { titleKey: "seo.farms.title", descKey: "seo.farms.desc" },
  "/products": { titleKey: "seo.products.title", descKey: "seo.products.desc" },
  "/contact": { titleKey: "seo.contact.title", descKey: "seo.contact.desc" },
  "/login": { titleKey: "seo.login.title", descKey: "seo.login.desc" },
  "/signup": { titleKey: "seo.signup.title", descKey: "seo.signup.desc" },
  "/cart": { titleKey: "seo.cart.title", descKey: "seo.cart.desc" },
  "/dashboard": { titleKey: "seo.dashboard.title", descKey: "seo.dashboard.desc" },
};

const setMeta = (name, content) => {
  const isOgTag = name.startsWith("og:");
  const selector = isOgTag ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let el = document.querySelector(selector);

  if (!el) {
    el = document.createElement("meta");
    if (isOgTag) {
      el.setAttribute("property", name);
    } else {
      el.setAttribute("name", name);
    }
    document.head.appendChild(el);
  }

  el.setAttribute("content", content);
};

const SEO = () => {
  const { pathname } = useLocation();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : BASE_URL;
    const normalizedPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
    const basePath = "/" + normalizedPath.split("/").filter(Boolean).slice(0, 1).join("/");
    const route = routeMeta[normalizedPath] || routeMeta[basePath] || routeMeta["/"];
    const activeLanguage = i18n.resolvedLanguage || i18n.language || "al";
    const localizedHomeMeta = HOME_META[activeLanguage] || HOME_META.al;
    const localizedDefaultKeywords =
      DEFAULT_KEYWORDS[activeLanguage] || DEFAULT_KEYWORDS.al;
    const isHome = normalizedPath === "/";
    const title = isHome
      ? localizedHomeMeta.title
      : t(route.titleKey, { defaultValue: SITE });
    const desc = isHome
      ? localizedHomeMeta.desc
      : t(route.descKey, { defaultValue: "" });
    const keywords = isHome ? localizedHomeMeta.keywords : localizedDefaultKeywords;
    const fullTitle = isHome ? title : `${title} | ${SITE}`;
    const currentUrl = `${origin}${normalizedPath}`;
    const ogImage = `${origin}/logo512.png`;
    const locale = activeLanguage === "al" ? "sq_AL" : "en_US";

    document.title = fullTitle;
    setMeta("description", desc);
    setMeta("keywords", keywords);
    setMeta("robots", "index, follow, max-image-preview:large");
    setMeta("og:site_name", SITE);
    setMeta("og:type", "website");
    setMeta("og:locale", locale);
    setMeta("og:title", fullTitle);
    setMeta("og:description", desc);
    setMeta("og:url", currentUrl);
    setMeta("og:image", ogImage);
    setMeta("og:image:alt", "Dora Fshatit logo");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", desc);
    setMeta("twitter:image", ogImage);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", currentUrl);
  }, [pathname, t, i18n.language, i18n.resolvedLanguage]);

  return null;
};

export default SEO;
